import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, LogIn, LogOut, Clock, Trash2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function InternDailyLog() {
  const [logs, setLogs] = useState([]);
  const [hoursByDate, setHoursByDate] = useState({});  // YYYY-MM-DD → minutes_online
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: today(), summary: "", blockers: "" });
  const [busy, setBusy] = useState(false);
  const [showAllLogs, setShowAllLogs] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [logsR, attR] = await Promise.all([
        api.get("/intern/logs/mine?days=60"),
        api.get("/employee/attendance?days=60").catch(() => ({ data: [] })),
      ]);
      setLogs(logsR.data || []);
      const m = {};
      (attR.data || []).forEach((d) => { m[d.date] = d.minutes_online || 0; });
      setHoursByDate(m);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const fmtHrs = (mins) => mins ? `${Math.floor(mins / 60)}h ${mins % 60}m` : "—";

  const clock = async (action) => {
    try {
      await api.post("/intern/clock", { action });
      toast.success(action === "in" ? "Clocked in" : "Clocked out");
    } catch (e) { toast.error("Couldn't update clock"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.summary.trim()) { toast.error("Summary is required"); return; }
    setBusy(true);
    try {
      // Hours auto-computed from clock-in/out + activity heartbeats — server
      // already stores this; we just relay the day's bucket count.
      const minutes = hoursByDate[form.date] || 0;
      await api.post("/intern/logs", { ...form, hours: minutes ? +(minutes / 60).toFixed(2) : null });
      toast.success("Log saved");
      setForm({ date: today(), summary: "", blockers: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (l) => {
    if (!window.confirm("Delete this log?")) return;
    try {
      await api.delete(`/intern/logs/${l.id}`);
      setLogs((arr) => arr.filter((x) => x.id !== l.id));
    } catch (e) { toast.error("Couldn't delete"); }
  };

  return (
    <div data-testid="intern-daily-log-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">INTERN PROGRAM</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Daily <em className="italic text-kb-primary">progress log</em></h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">Record a short summary of what you worked on. Every submission is preserved as its own timestamped entry, so the full history is always visible.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => clock("in")} className="kb-btn-outline text-sm" data-testid="intern-clock-in-btn">
            <LogIn className="w-4 h-4" /> Clock in
          </button>
          <button onClick={() => clock("out")} className="kb-btn-outline text-sm" data-testid="intern-clock-out-btn">
            <LogOut className="w-4 h-4" /> Clock out
          </button>
        </div>
      </div>

      <form onSubmit={submit} className="kb-card p-6 mt-6 space-y-3" data-testid="intern-log-form">
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Date</label>
            <input type="date" className="kb-input mt-1" value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="intern-log-date" />
          </div>
          <div className="sm:col-span-2">
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Hours worked (auto)</label>
            <div className="mt-1 kb-input bg-slate-50 flex items-center justify-between" data-testid="intern-log-hours-auto">
              <span className="font-mono text-kb-primary">{fmtHrs(hoursByDate[form.date])}</span>
              <span className="kb-mono text-[10px] text-kb-ink-muted">from clock-in/out + activity</span>
            </div>
          </div>
        </div>
        <div>
          <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">What did you work on? *</label>
          <textarea required rows={4} className="kb-input mt-1 resize-none" placeholder="Short summary of today's progress…"
            value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="intern-log-summary" />
        </div>
        <div>
          <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Blockers / next steps</label>
          <textarea rows={2} className="kb-input mt-1 resize-none" placeholder="Anything blocking you? What's next?"
            value={form.blockers} onChange={(e) => setForm({ ...form, blockers: e.target.value })} data-testid="intern-log-blockers" />
        </div>
        <button type="submit" disabled={busy} className="kb-btn-primary text-sm" data-testid="intern-log-submit-btn">
          <Plus className="w-4 h-4" /> {busy ? "Saving…" : "Save log"}
        </button>
      </form>

      <h2 className="font-serif text-xl text-kb-ink mt-10">Recent logs</h2>
      {loading ? <div className="mt-4 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        logs.length === 0 ? (
          <div className="mt-4 text-center py-10 border border-dashed border-slate-300 rounded-sm bg-white">
            <Clock className="w-7 h-7 text-kb-ink-light mx-auto" strokeWidth={1.5} />
            <p className="text-sm text-kb-ink-muted mt-2">No logs yet — start your first one above.</p>
          </div>
        ) : (
          <>
            <div className="mt-4 space-y-3">
              {(showAllLogs ? logs : logs.slice(0, 2)).map((l) => (
                <div key={l.id} className="kb-card p-4" data-testid={`intern-log-${l.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                        {l.date}{l.created_at ? <> · <span className="normal-case tracking-normal">{new Date(l.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span></> : null}{l.hours ? <> · {l.hours} h</> : null}
                      </div>
                      <p className="text-sm text-kb-ink mt-1 whitespace-pre-line">{l.summary}</p>
                      {l.blockers && (
                        <div className="mt-2 text-xs text-kb-ink-muted italic">Blockers: {l.blockers}</div>
                      )}
                    </div>
                    <button onClick={() => remove(l)} className="text-rose-600 hover:text-rose-700 p-1" data-testid={`intern-log-${l.id}-delete-btn`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {logs.length > 2 && (
              <button
                type="button"
                onClick={() => setShowAllLogs((v) => !v)}
                className="mt-4 kb-btn-outline text-xs"
                data-testid="intern-log-toggle-history-btn"
              >
                {showAllLogs ? `Show less` : `Show all ${logs.length} logs`}
              </button>
            )}
          </>
        )}
    </div>
  );
}
